import { useEffect } from "react";
import { mediaUrl } from "../mediaUrl";

interface Font {
  name: string;
  preview: string;
  has_preview?: boolean;
}

interface FontSelectorProps {
  apiUrl: string;
  fonts: Font[];
  selectedFont: string;
  onSelectFont: (fontName: string) => void;
  showFontSelector: boolean;
  onToggleSelector: () => void;
  /** Defaults to "Font" -- callers with more specific context (e.g. "Embroidery Font") can override it. */
  label?: string;
}

// Font names can contain a literal "/" (e.g. "Ink/Stitch Masego"), which the
// {font_name:path} route expects verbatim -- hence encodeURI, not
// encodeURIComponent.
function previewSrc(apiUrl: string, fontName: string): string {
  return mediaUrl(apiUrl, `/fonts/preview/${encodeURI(fontName)}`);
}

export function FontSelector({
  apiUrl,
  fonts,
  selectedFont,
  onSelectFont,
  showFontSelector,
  onToggleSelector,
  label = "Font",
}: FontSelectorProps) {
  // Close font selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showFontSelector && !target.closest('[data-font-selector]')) {
        onToggleSelector();
      }
    };

    if (showFontSelector) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showFontSelector, onToggleSelector]);

  const selectedFontData = fonts.find(f => f.name === selectedFont);

  return (
    <label>
      {label}
      <div style={{ position: 'relative' }}>
        <div
          data-font-selector
          style={{
            border: '1px solid var(--pico-form-element-border-color)',
            borderRadius: 'var(--pico-border-radius)',
            padding: '0.5rem',
            backgroundColor: 'var(--pico-form-element-background-color)',
            cursor: 'pointer',
            minHeight: '2.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            // Nothing escapes the control. Font previews are arbitrarily wide
            // images, and without this a wide one pushes the caret and the
            // font name off the right edge of a phone screen - taking the
            // whole page's horizontal scroll with it.
            overflow: 'hidden',
          }}
          onClick={onToggleSelector}
        >
          {selectedFont ? (
            // flex + minWidth:0, not width:100%. width:100% made this claim the
            // entire row, leaving the caret below no space at all.
            // minWidth:0 is what actually lets the name truncate: flex items
            // default to min-width:auto and refuse to shrink past their content.
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
              {selectedFontData?.has_preview ? (
                // The preview *is* the font's name, set in the font itself, so
                // it stands alone -- printing the name beside it read as a
                // rendering bug ("Barstitch Bold  Barstitch Bold"). The name
                // still reaches assistive tech and hover through alt/title.
                <img
                  src={previewSrc(apiUrl, selectedFont)}
                  alt={selectedFont}
                  title={selectedFont}
                  style={{ height: '24px', maxWidth: '100%', objectFit: 'contain' }}
                />
              ) : (
                // Nothing to render the name *in*, so render it as text --
                // otherwise this font would have no visible label at all.
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFont}
                </span>
              )}
            </div>
          ) : (
            <span style={{ color: 'var(--pico-muted-color)' }}>Select a font...</span>
          )}
          <span style={{ marginLeft: 'auto', flexShrink: 0 }}>▼</span>
        </div>

        <div style={{
          position: 'absolute',
          zIndex: 1000,
          backgroundColor: 'var(--pico-card-background-color)',
          border: '1px solid var(--pico-form-element-border-color)',
          borderRadius: 'var(--pico-border-radius)',
          marginTop: '0.25rem',
          // 400px is most of a phone screen, and an open list that covers the
          // viewport hides the field it belongs to.
          maxHeight: 'min(400px, 50vh)',
          overflowY: 'auto',
          left: 0,
          right: 0,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          display: showFontSelector ? 'block' : 'none',
          pointerEvents: showFontSelector ? 'auto' : 'none',
        }}>
          {fonts.map((font) => (
            <div
              key={font.name}
              onClick={(e) => {
                e.stopPropagation();
                onSelectFont(font.name);
              }}
              style={{
                padding: '0.75rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                borderBottom: '1px solid var(--pico-muted-border-color)',
                backgroundColor: selectedFont === font.name ? 'var(--pico-primary-background)' : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (selectedFont !== font.name) {
                  e.currentTarget.style.backgroundColor = 'var(--pico-form-element-background-color)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedFont !== font.name) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {font.has_preview ? (
                // One label per row, and it is this image: every preview is
                // the font's own name set in that font, so pairing it with a
                // plain-text copy of the same name looked like a duplicate.
                // Now the list is a specimen sheet, which is the point of it.
                <img
                  src={previewSrc(apiUrl, font.name)}
                  alt={font.name}
                  title={font.name}
                  loading="lazy"
                  style={{
                    height: '40px',
                    // Bounded rather than left at natural width: previews are
                    // arbitrarily wide, and a long name would otherwise force
                    // the whole list to scroll sideways on a phone.
                    maxWidth: '100%',
                    objectFit: 'contain',
                    minWidth: 0,
                  }}
                />
              ) : (
                // No specimen to show, so the name has to carry the row.
                <span style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{font.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </label>
  );
}
